import React from 'react';
import cn from '@/utils/cn';
import SPAPill from '../shared/SPAPill';
import type { SPAPillTone } from '../shared/SPAPill';

export type FindingOwner = 'tci' | 'lms' | 'join';

export interface Finding {
  code: string;
  severity: 'error' | 'warning';
  rank: number;
  owner?: FindingOwner;
  title: string;
  detail: string;
}

// AIDEV-NOTE: The owner answers the question support actually opens the page with — is this mine to
// fix, the teacher's, or the link between us? Every case worked in Sept 2026 hinged on it: a
// Blueprint-only assignment and a mis-claimed column look identical until you know who can act.
// AIDEV-NOTE: SPAPill, not hand-rolled classes. `tw-bg-spa-accent/15` computes to transparent —
// the opacity-slash syntax does not resolve against these CSS-variable tokens, so the badge rendered
// as bare text with no pill. SPAPill builds its tint with color-mix, which does work in both themes.
const OWNERS: Record<FindingOwner, { label: string; title: string; tone: SPAPillTone }> = {
  join: {
    label: 'LINK',
    title: 'The pointer between TCI and the LMS is wrong — fixable here',
    tone: 'warning',
  },
  lms: {
    label: 'LMS',
    title: 'Only the teacher can fix this, in the LMS — no data fix exists on our side',
    tone: 'info',
  },
  tci: {
    label: 'TCI',
    title: 'Ours to change — usually a duplicate decision or a config fix',
    tone: 'accent',
  },
};

const TONE = {
  error: cn(
    'tw-border-[color:color-mix(in_srgb,var(--spa-text-error)_40%,transparent)]',
    'tw-bg-[color:color-mix(in_srgb,var(--spa-text-error)_5%,transparent)]',
  ),
  warning: cn(
    'tw-border-[color:color-mix(in_srgb,var(--spa-text-warning)_40%,transparent)]',
    'tw-bg-[color:color-mix(in_srgb,var(--spa-text-warning)_5%,transparent)]',
  ),
};

const DOT = {
  error: 'tw-bg-spa-danger-solid',
  warning: 'tw-bg-spa-warning',
};

/** Ranked worst-first — the top one is almost always the thing to act on. */
export const ranked = (findings: Finding[]) => (
  [...findings].sort((a, b) => b.rank - a.rank)
);

const Findings = ({ findings }: { findings: Finding[] }) => {
  if (findings.length === 0) {
    return (
      <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
        No wiring faults detected. If scores still aren&apos;t landing, the cause is outside what we
        can see from our side — check the column in the LMS itself.
      </p>
    );
  }

  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
      {ranked(findings).map(finding => (
        <li
          key={finding.code}
          className={cn('tw-rounded tw-border tw-border-solid tw-px-3 tw-py-2', TONE[finding.severity])}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <span aria-hidden="true" className={cn('tw-h-2 tw-w-2 tw-rounded-full', DOT[finding.severity])} />
            {finding.owner != null && (
              <SPAPill
                className="tw-font-mono tw-text-xs"
                title={OWNERS[finding.owner].title}
                tone={OWNERS[finding.owner].tone}
              >
                {OWNERS[finding.owner].label}
              </SPAPill>
            )}
            <span className="tw-text-sm tw-font-semibold tw-text-spa-primary">{finding.title}</span>
          </div>
          <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">{finding.detail}</p>
        </li>
      ))}
    </ul>
  );
};

export default Findings;
